/**
 * MySessionHistoryCard — iter189 Phase 6.
 * Sports-only Overview card: shows the current user's attendance over the
 * last 5 completed sessions + an attendance % gauge. Helps members spot
 * "wait, I've been marked absent twice" issues quickly.
 *
 * Hidden when (a) group has no sessions, (b) current user wasn't in any
 * completed session, (c) typeConfig.isSports is false.
 */
import { useEffect, useState } from 'react';
import { ListChecks, Target, History } from 'lucide-react';
import api from '../lib/api';
import { Card } from './ui/card';

export default function MySessionHistoryCard({ ctx, typeConfig }) {
  const group = ctx?.group;
  const userId = ctx?.user?.id;
  const groupId = group?.id;
  const [rows, setRows] = useState(null); // null = loading, [] = no data, [...] = ready

  useEffect(() => {
    if (!typeConfig?.isSports || !groupId || !userId) {
      setRows([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/groups/${groupId}/sessions`);
        if (cancelled) return;
        // Filter completed sessions where this user was involved
        const completed = (res.data || [])
          .filter((s) => s.status === 'completed')
          .filter((s) => {
            const ids = [
              ...(s.present_ids || []),
              ...(s.absent_ids || []),
              ...(s.late_under_ids || []),
              ...(s.late_above_ids || []),
              ...(s.confirmed_ids || []),
            ];
            return ids.includes(userId);
          })
          .sort((a, b) => (b.date || '').localeCompare(a.date || ''))
          .slice(0, 5);
        setRows(completed);
      } catch {
        setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [groupId, userId, typeConfig]);

  if (!typeConfig?.isSports || !groupId || !userId) return null;
  if (rows === null) return null; // loading silently
  if (rows.length === 0) return null;

  // Tag each row with the user's outcome
  const enriched = rows.map((s) => {
    let outcome = 'unknown', tone = 'bg-muted text-muted-foreground';
    if (s.present_ids?.includes(userId))            { outcome = 'Present';     tone = 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'; }
    else if (s.late_under_ids?.includes(userId))    { outcome = 'Late ≤10m';   tone = 'bg-amber-500/15 text-amber-700 dark:text-amber-300'; }
    else if (s.late_above_ids?.includes(userId))    { outcome = 'Late >10m';   tone = 'bg-orange-500/15 text-orange-700 dark:text-orange-300'; }
    else if (s.absent_ids?.includes(userId))        { outcome = 'No-show';     tone = 'bg-rose-500/15 text-rose-700 dark:text-rose-300'; }
    return { ...s, _outcome: outcome, _tone: tone };
  });
  const presentCount = enriched.filter((r) => r._outcome === 'Present' || r._outcome === 'Late ≤10m' || r._outcome === 'Late >10m').length;
  const pct = Math.round((presentCount / enriched.length) * 100);

  return (
    <Card className="p-4 rounded-2xl border-2 border-border bg-card" data-testid="my-session-history-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <History className="w-4 h-4 text-primary" />
          <h3 className="font-bold text-sm tracking-tight">My recent sessions</h3>
        </div>
        <div className={`px-2.5 py-1 rounded-full text-xs font-bold tabular-nums flex items-center gap-1 ${
          pct >= 80 ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' :
          pct >= 50 ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300' :
                      'bg-rose-500/15 text-rose-700 dark:text-rose-300'
        }`} data-testid="my-session-history-pct">
          <Target className="w-3 h-3" />
          {pct}% attended
        </div>
      </div>
      <div className="space-y-1.5">
        {enriched.map((s) => (
          <div key={s.id} className="flex items-center justify-between text-xs py-1" data-testid={`session-row-${s.id}`}>
            <div className="min-w-0 flex-1 mr-2">
              <p className="font-semibold truncate">{s.title || 'Session'}</p>
              <p className="text-xs text-muted-foreground">{s.date}</p>
            </div>
            <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-bold ${s._tone}`}>
              {s._outcome}
            </span>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground mt-2 italic flex items-center gap-1">
        <ListChecks className="w-3 h-3" /> Last {enriched.length} completed session{enriched.length === 1 ? '' : 's'}.
      </p>
    </Card>
  );
}
